import { t } from "../../../i18n/locale";

/**
 * Display metadata for the production-phase codes of the dynamic phase model.
 *
 * Shared by the Gantt (bar colours, labels, legend) and by the swim-lane
 * builder, which needs the canonical order to sort phase lanes.
 */
export interface PhaseMeta {
  color: string;
  /** Short text drawn inside a bar. */
  label: string;
  name: string;
}

export const PHASE_META: Record<string, PhaseMeta> = {
  mixing: { color: "#4b9987", label: "Mix", name: "Miješanje" },
  dividing: { color: "#16a085", label: "Dij", name: "Dijeljenje" },
  bulk_fermentation: { color: "#5b8dd9", label: "Ferm", name: "Fermentacija" },
  moulding: { color: "#8e44ad", label: "Obl", name: "Oblikovanje" },
  processing: { color: "#e8c547", label: "Obr", name: "Obrada" },
  final_proof: { color: "#9b59b6", label: "Zav", name: "Završno dizanje" },
  baking: { color: "#e67e22", label: "Peč", name: "Pečenje" },
  cooling: { color: "#5bc0de", label: "Hlađ", name: "Hlađenje" },
  packaging: { color: "#27ae60", label: "Pak", name: "Pakiranje" },
};

/** Canonical render order for phase lanes, the legend and fallback bars. */
export const PHASE_ORDER = [
  "mixing",
  "dividing",
  "bulk_fermentation",
  "moulding",
  "processing",
  "final_proof",
  "baking",
  "cooling",
  "packaging",
];

/** Legacy 5-phase codes used when a batch has no dynamic Phases array. */
export const LEGACY_PHASE_CODES = [
  "mixing",
  "bulk_fermentation",
  "processing",
  "final_proof",
  "baking",
];

export const DEFAULT_PHASE_COLOR = "#7f8c8d";

export function phaseColor(code: string): string {
  return PHASE_META[code]?.color ?? DEFAULT_PHASE_COLOR;
}

export function phaseLabel(code: string): string {
  const label = PHASE_META[code]?.label;
  return label ? t(label) : code ? code.slice(0, 3) : "—";
}

export function phaseName(code: string): string {
  const name = PHASE_META[code]?.name;
  return name ? t(name) : code;
}

/** Sort key for phase codes; codes outside the routing order come last. */
export function phaseRank(code: string): number {
  const index = PHASE_ORDER.indexOf(code);
  return index === -1 ? PHASE_ORDER.length : index;
}
